import { z } from 'zod';
import { SURVEY_OPTION_KEYS, SURVEY_QUESTION_IDS } from './asset-group-criticality-survey.constants.js';

const objectIdSchema = z.string().min(1);

export const EvaluationStatusSchema = z.enum(['undefined', 'passive', 'active', 'completed']);

export const CurrentSurveySchema = z.object({
    description: z.string().min(1),
    endDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    startedAt: z.string().datetime().optional(),
    startedBy: objectIdSchema.optional(),
    roundNumber: z.number().int().min(1),
});

export const EvaluationApprovalSchema = z.object({
    criticalityDegree: z.union([z.literal(1), z.literal(2), z.literal(3)]),
    totalScore: z.number().int(),
    approvedAt: z.string().datetime().optional(),
    approvedBy: objectIdSchema.optional(),
    approvedByName: z.string().optional(),
    coordinatorUserId: objectIdSchema.optional(),
    approvalAuthorityName: z.string().optional(),
    approvalSignature: z.string().optional(),
});

export const AssetGroupCriticalityEvaluationSchema = z.object({
    id: objectIdSchema.optional(),
    assetGroupId: objectIdSchema,
    assetGroupName: z.string().optional(),
    status: EvaluationStatusSchema,
    expertUserIds: z.array(objectIdSchema).default([]),
    surveyRepeatCount: z.number().int().min(0).default(0),
    currentSurvey: CurrentSurveySchema.nullable().optional(),
    approval: EvaluationApprovalSchema.nullable().optional(),
    restartReason: z.string().optional(),
    responseCount: z.number().int().min(0).optional(),
    expertCount: z.number().int().min(0).optional(),
    canEvaluate: z.boolean().optional(),
    canRepeatSurvey: z.boolean().optional(),
    surveyClosed: z.boolean().optional(),
    createdAt: z.string().optional(),
    updatedAt: z.string().optional(),
});

export const MyCriticalitySurveyRowSchema = z.object({
    evaluationId: objectIdSchema,
    assetGroupId: objectIdSchema.optional(),
    assetGroupName: z.string().optional(),
    description: z.string().optional(),
    endDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
    roundNumber: z.number().int().min(1).optional(),
    completed: z.boolean(),
    closed: z.boolean(),
    responseId: objectIdSchema.nullable().optional(),
});

const surveyAnswersShape = Object.fromEntries(
    SURVEY_QUESTION_IDS.map((qId) => [
        qId,
        z.enum(SURVEY_OPTION_KEYS[qId]),
    ]),
);

export const SurveyAnswersSchema = z.object(surveyAnswersShape);

const surveyAnswerExplanationsShape = Object.fromEntries(
    SURVEY_QUESTION_IDS.map((qId) => [qId, z.string().max(2000).optional()]),
);

export const SurveyAnswerExplanationsSchema = z.object(surveyAnswerExplanationsShape).optional();

export const AssignExpertsSchema = z.object({
    expertUserIds: z.array(objectIdSchema).min(1),
});

export const StartSurveySchema = z.object({
    description: z.string().min(1),
    endDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    coordinatorUserId: objectIdSchema.optional(),
});

export const SubmitSurveySchema = z.object({
    answers: SurveyAnswersSchema,
    answerExplanations: SurveyAnswerExplanationsSchema,
});

export const ApproveEvaluationSchema = z.object({});

export const RestartEvaluationSchema = z.object({
    restartReason: z.string().min(1),
});

export const SurveyResponseSchema = z.object({
    id: objectIdSchema.optional(),
    evaluationId: objectIdSchema,
    assetGroupId: objectIdSchema,
    surveyRoundNumber: z.number().int().min(1),
    userId: objectIdSchema,
    userName: z.string().optional(),
    answers: SurveyAnswersSchema,
    answerExplanations: z.record(z.string(), z.string()).optional(),
    questionScores: z.record(z.string(), z.number()),
    totalScore: z.number().int(),
    criticalityDegree: z.union([z.literal(1), z.literal(2), z.literal(3)]),
    completedAt: z.string().optional(),
});
